"use client"

import type { GachaEvmPurchase } from "@anaconda/core/gacha/evm-purchase"
import { Trans } from "@lingui/react/macro"
import { usePrivy } from "@privy-io/react-auth"
import { wrap } from "@reatom/core"
import { reatomComponent } from "@reatom/react"
import { useQuery } from "@tanstack/react-query"
import { continueGachaEvmOpening } from "./gacha-evm-continue-opening"
import { gachaEvmPurchases } from "./gacha-evm-purchase-state"
import { useGachaApiOptions } from "./use-gacha-api-options"
import { useGachaNetworkContext } from "./use-gacha-network"

export const GachaEvmPendingActivity = reatomComponent(
  ({ records }: { records: GachaEvmPurchase[] }) => (
    <section
      aria-label="EVM Issued Pack Activity"
      className="mx-4 my-3 flex flex-col gap-3"
    >
      {records.map((record) => (
        <article
          key={record.id}
          className="rounded-lg border border-foreground/10 p-4 text-sm"
        >
          <div className="font-headline">
            {record.chain.name} · {record.machineCode}
          </div>
          <output className="mt-1 block">
            {record.state === "delivered" ? (
              <Trans>NFT delivered</Trans>
            ) : record.state === "payment_rejected" ||
              (record.state === "payment_pending" &&
                record.lastCode === "CLIENT_REJECTION_REPORTED") ? (
              <Trans>Payment rejection reported — purchase unresolved</Trans>
            ) : record.state === "payment_failed" ? (
              <Trans>
                Payment attempt reverted — purchase reconciliation pending
              </Trans>
            ) : record.state === "unpaid_cancelled" ? (
              <Trans>Cancelled before payment</Trans>
            ) : record.state === "unpaid_expired" ? (
              <Trans>Expired before payment was initiated</Trans>
            ) : record.state === "needs_support" ? (
              <Trans>Needs support — purchase unresolved</Trans>
            ) : record.evidence ? (
              <Trans>Payment confirmed — NFT delivery pending</Trans>
            ) : (
              <Trans>Purchase pending — payment not yet verified</Trans>
            )}
          </output>
          {record.state === "needs_support" && (
            <p className="mt-2 text-foreground/60">
              <Trans>
                Automated reconciliation stopped; operator review is required.
                Payment may still be unresolved. This is not a refund.
              </Trans>
            </p>
          )}
          <div className="mt-1 break-all font-mono text-xs text-foreground/60">
            {record.walletAddress}
          </div>
          <div className="mt-1 break-all font-mono text-xs text-foreground/60">
            {record.memo ?? record.id}
          </div>
          {record.award?.evm_token_id != null &&
            record.state === "delivered" && (
              <div className="mt-1 break-all font-mono text-xs">
                {record.chain.cardContract} · {record.award.evm_token_id}
              </div>
            )}
        </article>
      ))}
    </section>
  ),
  "GachaEvmPendingActivity",
)

// No wallet-wide recovery on page load. Only check purchases started this session.
export const GachaEvmPurchaseTracking = reatomComponent(() => {
  const context = useGachaNetworkContext()
  const options = useGachaApiOptions()
  const { user, authenticated } = usePrivy()
  const tracking = gachaEvmPurchases.deliveryTracking
  const pending = tracking
    .pending()
    .filter(
      (record) =>
        record.userId === user?.id &&
        record.environment === context.environment,
    )
  useQuery({
    queryKey: [
      "gacha-evm-session-delivery",
      user?.id,
      context.environment,
      options.bffBaseUrl,
      options.collectorCryptGachaBaseUrl,
    ],
    enabled:
      authenticated && pending.length > 0 && !gachaEvmPurchases.signingOwner(),
    retry: false,
    refetchInterval: pending.length > 0 ? 5000 : false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: wrap(async () => {
      await wrap(
        Promise.all(
          pending.map(async (record) => {
            if (!tracking.take(record.id)) return
            try {
              await wrap(continueGachaEvmOpening(record, options))
            } catch {
              // A failed check spends its budget but never discards the purchase.
            }
          }),
        ),
      )
      return null
    }),
  })
  return null
}, "GachaEvmPurchaseTracking")
